/**
 * Wiring the standard live actions to the overlay.
 *
 * This is the one-line half of 配信モード. `createStreamOverlay` draws rows;
 * this decides what a row is made of when the row came from the platform.
 *
 * It is a separate module from `client.ts` so that the client keeps knowing
 * nothing about the DOM. A game running headless, or under test, imports the
 * same `TRYPORTALGame` and pays nothing for a feature it did not switch on.
 *
 * ## Everything a live stream produces, and the game chooses
 *
 * There is one entry here per Canonical Event type the platform models — chat,
 * gifts, likes, shares, follows, subscriptions, the three membership moments,
 * room entries, money, and the start and end of the broadcast. `enableStreamMode()`
 * with no arguments claims all of them; `{ actions: ['CHAT', 'GIFT'] }` claims
 * two. Claiming one that the game's manifest never declared is harmless and
 * inert: the release has no definition for it, so the server refuses any rule
 * that names it and nothing is ever sent.
 *
 * That asymmetry is the design. The platform is built to deliver everything it
 * can see, and the *game* decides what it wants — not the SDK, and not by
 * having to know in advance which providers send what.
 *
 * ## The payloads are not provider payloads
 *
 * Everything read here comes from the platform's reserved `tp_live_*` actions,
 * whose schemas are fixed by
 * `packages/game-manifest/src/live-standard-actions.ts` and written into the
 * release by `publishing` regardless of what the game's manifest declared. A
 * TikTok frame and a YouTube frame produce the same shapes, and neither carries
 * a provider field name, a product name, or a converted currency (CLAUDE.md
 * invariants 7, 8, 19 and 21). A gift is a label and a positive count. Money is
 * whatever the provider itself called money, in micros, beside its own ISO 4217
 * code, with no arithmetic relating it to anything else.
 *
 * ## The schemas below are a second copy, on purpose
 *
 * `ActionProcessor` validates every payload against whatever the game
 * registered, so registering a permissive validator for a reserved action would
 * mean the game accepted a shape the platform never promised. They are checked
 * against the manifest package's originals by
 * `tests/contract/standard-live-actions.test.ts`, which is what stops the copy
 * drifting. The values are still read defensively below, because a validator
 * proves the shape and not the contents.
 */
import { createStreamOverlay, } from './stream-overlay.js';
/**
 * The reserved keys, by the name this module calls them.
 *
 * Keep in step with `live-standard-actions.ts`; a contract test asserts that the
 * two sets are equal and in the same order.
 */
export const STANDARD_LIVE_ACTION_KEYS = {
    CHAT: 'tp_live_chat',
    GIFT: 'tp_live_gift',
    FOLLOW: 'tp_live_follow',
    LIKE: 'tp_live_like',
    SHARE: 'tp_live_share',
    SUBSCRIBE: 'tp_live_subscribe',
    MEMBERSHIP: 'tp_live_membership',
    MEMBERSHIP_GIFT: 'tp_live_membership_gift',
    MEMBER_JOIN: 'tp_live_member_join',
    JOIN: 'tp_live_join',
    FUNDING: 'tp_live_funding',
    STREAM_START: 'tp_live_stream_start',
    STREAM_END: 'tp_live_stream_end',
};
/** Every kind, in the catalogue's own order. */
export const STANDARD_LIVE_ACTION_KINDS = Object.keys(STANDARD_LIVE_ACTION_KEYS);
/* ------------------------------------------------------------- schemas -- */
const ACTOR_SCHEMA = {
    type: 'object',
    required: ['ref', 'label'],
    properties: { ref: { type: 'string' }, label: { type: 'string' } },
};
/** `{ actor }` plus whatever else, with `actor` required. */
function withActor(properties = {}, required = []) {
    return {
        type: 'object',
        required: ['actor', ...required],
        properties: { actor: ACTOR_SCHEMA, ...properties },
    };
}
const INTEGER = { type: 'integer' };
const STRING = { type: 'string' };
const SCHEMAS = {
    CHAT: withActor({ text: STRING }, ['text']),
    GIFT: withActor({ giftLabel: STRING, count: INTEGER }, ['giftLabel', 'count']),
    FOLLOW: withActor(),
    LIKE: withActor({ count: INTEGER }, ['count']),
    SHARE: withActor(),
    SUBSCRIBE: withActor({ months: INTEGER }),
    MEMBERSHIP: withActor({ months: INTEGER, text: STRING, level: STRING }),
    MEMBERSHIP_GIFT: withActor({ count: INTEGER, level: STRING }, ['count']),
    MEMBER_JOIN: withActor({ level: STRING }),
    JOIN: withActor(),
    FUNDING: withActor({ amountMicros: INTEGER, currency: STRING, text: STRING }),
    // No `required`: the platform sends these with an empty payload unless a
    // provider happens to name the broadcaster.
    STREAM_START: { type: 'object', properties: { actor: ACTOR_SCHEMA } },
    STREAM_END: { type: 'object', properties: { actor: ACTOR_SCHEMA } },
};
/* --------------------------------------------------------------- reading -- */
function actorOf(payload) {
    const actor = payload['actor'];
    if (typeof actor !== 'object' || actor === null)
        return { ref: '', label: '' };
    const record = actor;
    return {
        ref: typeof record['ref'] === 'string' ? record['ref'] : '',
        label: typeof record['label'] === 'string' ? record['label'] : '',
    };
}
/**
 * A count the caller can use without checking it.
 *
 * The platform sends a positive delta, never a running total, and the schema
 * above already refused a non-integer. Clamping is belt and braces for a game
 * driving the overlay itself with hand-built values.
 */
function countOf(value, fallback = 1) {
    const parsed = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback;
    return Math.max(1, parsed);
}
/** An optional integer, or absent. */
function optionalInteger(value) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : undefined;
}
/** An optional non-empty string, or absent. */
function optionalText(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
/**
 * Turn on 配信モード for a connected game.
 *
 * ```js
 * const game = await connect();
 *
 * // Everything, drawn in the corner:
 * game.enableStreamMode();
 *
 * // Or: just the two things this game reacts to, drawn by the game itself.
 * game.enableStreamMode({
 *   actions: ['GIFT', 'LIKE'],
 *   render: false,
 *   onGift: ({ label, giftLabel, count }) => spawnFor(label, giftLabel, count),
 *   onLike: ({ count }) => addHeat(count),
 * });
 * ```
 */
export function enableStreamMode(game, options = {}) {
    const wanted = new Set(options.actions ?? STANDARD_LIVE_ACTION_KINDS);
    const render = options.render ?? true;
    const unregisters = [];
    // The overlay is created *after* the handlers are registered, and only if
    // every one of them succeeded.
    //
    // `ActionProcessor.register` throws on a duplicate key, which is the correct
    // behaviour and is exactly what a second `enableStreamMode()` call does. With
    // the old order that throw happened after the DOM existed, and the caller never
    // received the handle that could have removed it: a permanently orphaned layer,
    // stacked over the first one, with interleaved rows and no way to reach it.
    let overlay = null;
    const show = (entry) => {
        overlay?.present(entry);
    };
    /**
     * Register one kind, or do nothing when the game did not claim it.
     *
     * `read` turns a validated payload into the event; everything after it —
     * drawing the row, the general callback, the specific callback — is the same
     * for every kind and is written once.
     */
    const claim = (kind, read, notify) => {
        if (!wanted.has(kind))
            return;
        try {
            unregisters.push(game.onAction(STANDARD_LIVE_ACTION_KEYS[kind], {
                validate: SCHEMAS[kind],
                handle: (payload) => {
                    const event = read(payload);
                    show(event);
                    options.onLiveEvent?.(event);
                    notify(event);
                },
            }));
        }
        catch (error) {
            for (const off of unregisters)
                off();
            unregisters.length = 0;
            throw error;
        }
    };
    claim('CHAT', (payload) => ({
        kind: 'CHAT',
        ...actorOf(payload),
        text: typeof payload['text'] === 'string' ? payload['text'] : '',
    }), (event) => options.onChat?.(event));
    claim('GIFT', (payload) => ({
        kind: 'GIFT',
        ...actorOf(payload),
        giftLabel: typeof payload['giftLabel'] === 'string' ? payload['giftLabel'] : '',
        count: countOf(payload['count']),
    }), (event) => options.onGift?.(event));
    claim('FOLLOW', (payload) => ({ kind: 'FOLLOW', ...actorOf(payload) }), (event) => options.onFollow?.(event));
    claim('LIKE', (payload) => ({ kind: 'LIKE', ...actorOf(payload), count: countOf(payload['count']) }), (event) => options.onLike?.(event));
    claim('SHARE', (payload) => ({ kind: 'SHARE', ...actorOf(payload) }), (event) => options.onShare?.(event));
    claim('SUBSCRIBE', (payload) => {
        const months = optionalInteger(payload['months']);
        return { kind: 'SUBSCRIBE', ...actorOf(payload), ...(months === undefined ? {} : { months }) };
    }, (event) => options.onSubscribe?.(event));
    claim('MEMBERSHIP', (payload) => {
        const months = optionalInteger(payload['months']);
        const text = optionalText(payload['text']);
        const level = optionalText(payload['level']);
        return {
            kind: 'MEMBERSHIP',
            ...actorOf(payload),
            ...(months === undefined ? {} : { months }),
            ...(text === undefined ? {} : { text }),
            ...(level === undefined ? {} : { level }),
        };
    }, (event) => options.onMembership?.(event));
    claim('MEMBERSHIP_GIFT', (payload) => {
        const level = optionalText(payload['level']);
        return {
            kind: 'MEMBERSHIP_GIFT',
            ...actorOf(payload),
            count: countOf(payload['count']),
            ...(level === undefined ? {} : { level }),
        };
    }, (event) => options.onMembershipGift?.(event));
    claim('MEMBER_JOIN', (payload) => {
        const level = optionalText(payload['level']);
        return { kind: 'MEMBER_JOIN', ...actorOf(payload), ...(level === undefined ? {} : { level }) };
    }, (event) => options.onMemberJoin?.(event));
    claim('JOIN', (payload) => ({ kind: 'JOIN', ...actorOf(payload) }), (event) => options.onJoin?.(event));
    claim('FUNDING', (payload) => {
        const amountMicros = optionalInteger(payload['amountMicros']);
        const currency = optionalText(payload['currency']);
        const text = optionalText(payload['text']);
        // Together or not at all: an amount with no currency is a number nobody
        // can read, and a currency with no amount says nothing.
        const money = amountMicros !== undefined && currency !== undefined;
        return {
            kind: 'FUNDING',
            ...actorOf(payload),
            ...(money ? { amountMicros, currency } : {}),
            ...(text === undefined ? {} : { text }),
        };
    }, (event) => options.onFunding?.(event));
    claim('STREAM_START', (payload) => ({ kind: 'STREAM_START', ...actorOf(payload) }), (event) => options.onStreamStart?.(event));
    claim('STREAM_END', (payload) => ({ kind: 'STREAM_END', ...actorOf(payload) }), (event) => options.onStreamEnd?.(event));
    if (render)
        overlay = createStreamOverlay(options);
    return {
        get overlay() {
            return overlay;
        },
        destroy() {
            for (const off of unregisters)
                off();
            unregisters.length = 0;
            overlay?.destroy();
            overlay = null;
        },
    };
}
